import React, { useMemo, useState } from "react";
import {
  Home,
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import "../../styles/PropertyTypes.css";

const initialPropertyTypes = [
  {
    id: 1,
    name: "1 BHK",
    description: "One bedroom residential property",
    status: "Active",
  },
  {
    id: 2,
    name: "2 BHK",
    description: "Two bedroom residential property",
    status: "Active",
  },
  {
    id: 3,
    name: "3 BHK",
    description: "Three bedroom residential property",
    status: "Active",
  },
  {
    id: 4,
    name: "4 BHK",
    description: "Four bedroom residential property",
    status: "Active",
  },
  {
    id: 5,
    name: "5 BHK",
    description: "Five bedroom residential property",
    status: "Active",
  },
  {
    id: 6,
    name: "Villa",
    description: "Independent villa or bungalow",
    status: "Active",
  },
  {
    id: 7,
    name: "Independent House",
    description: "Standalone residential house",
    status: "Active",
  },
  {
    id: 8,
    name: "Office",
    description: "Office or commercial workspace",
    status: "Active",
  },
  {
    id: 9,
    name: "Shop",
    description: "Retail shop or showroom",
    status: "Inactive",
  },
  {
    id: 10,
    name: "Warehouse",
    description: "Warehouse or storage facility",
    status: "Active",
  },
];

export default function PropertyTypes() {
  const [propertyTypes, setPropertyTypes] = useState(
    initialPropertyTypes
  );

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 7;

  const filteredData = useMemo(() => {
    return propertyTypes.filter((item) => {
      const searchValue = search.toLowerCase();

      const matchesSearch =
        item.name.toLowerCase().includes(searchValue) ||
        item.description.toLowerCase().includes(searchValue);

      const matchesStatus =
        !statusFilter || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [propertyTypes, search, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredData.length / rowsPerPage)
  );

  const currentPage = Math.min(page, totalPages);

  const currentRows = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const activeCount = propertyTypes.filter(
    (item) => item.status === "Active"
  ).length;

  const inactiveCount = propertyTypes.filter(
    (item) => item.status === "Inactive"
  ).length;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const toggleStatus = (id) => {
    setPropertyTypes((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status:
                item.status === "Active"
                  ? "Inactive"
                  : "Active",
            }
          : item
      )
    );
  };

  const deletePropertyType = (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this property type?"
    );

    if (!confirmed) return;

    setPropertyTypes((prev) =>
      prev.filter((item) => item.id !== id)
    );
  };

  const handleAdd = () => {
    alert("Add Property Type form will be connected here.");
  };

  const handleEdit = (item) => {
    alert(`Edit Property Type: ${item.name}`);
  };

  return (
    <div className="property-types-page">

      {/* Header */}
      <div className="property-types-header">

        <div className="property-types-title">

          <div className="property-types-icon">
            <Home size={22} />
          </div>

          <div>
            <h1>House Types</h1>
            <p>
              Manage House types used for moving quotations
            </p>
          </div>

        </div>

        <button
          className="property-add-btn"
          onClick={handleAdd}
        >
          <Plus size={18} />
          Add House Type
        </button>

      </div>

      {/* Summary Cards */}
      <div className="property-summary-grid">

        <div className="property-summary-card">

          <div className="property-summary-icon total">
            <Home size={20} />
          </div>

          <div>
            <span>Total Types</span>
            <strong>{propertyTypes.length}</strong>
          </div>

        </div>

        <div className="property-summary-card">

          <div className="property-summary-icon active">
            <CheckCircle2 size={20} />
          </div>

          <div>
            <span>Active Types</span>
            <strong>{activeCount}</strong>
          </div>

        </div>

        <div className="property-summary-card">

          <div className="property-summary-icon inactive">
            <XCircle size={20} />
          </div>

          <div>
            <span>Inactive Types</span>
            <strong>{inactiveCount}</strong>
          </div>

        </div>

      </div>

      {/* Filters */}
      <div className="property-filter-card">

        <div className="property-search">

          <Search size={17} />

          <input
            type="text"
            placeholder="Search property type..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>

        {(search || statusFilter) && (
          <button
            className="property-clear-btn"
            onClick={clearFilters}
          >
            <X size={15} />
            Clear
          </button>
        )}

      </div>

      {/* Desktop Table */}
      <div className="property-table-card">

        <div className="property-table-wrapper">

          <table className="property-table">

            <thead>
              <tr>
                <th>#</th>
                <th>Property Type</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {currentRows.length > 0 ? (
                currentRows.map((item, index) => (
                  <tr key={item.id}>

                    <td>
                      {(currentPage - 1) * rowsPerPage +
                        index +
                        1}
                    </td>

                    <td>
                      <div className="property-name">

                        <div className="property-row-icon">
                          <Home size={16} />
                        </div>

                        <strong>{item.name}</strong>

                      </div>
                    </td>

                    <td>
                      <span className="property-description">
                        {item.description}
                      </span>
                    </td>

                    <td>

                      <button
                        className={`property-status ${
                          item.status === "Active"
                            ? "active"
                            : "inactive"
                        }`}
                        onClick={() =>
                          toggleStatus(item.id)
                        }
                      >
                        {item.status}
                      </button>

                    </td>

                    <td>

                      <div className="property-actions">

                        <button
                          className="property-action edit"
                          title="Edit"
                          onClick={() =>
                            handleEdit(item)
                          }
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          className="property-action delete"
                          title="Delete"
                          onClick={() =>
                            deletePropertyType(item.id)
                          }
                        >
                          <Trash2 size={16} />
                        </button>

                      </div>

                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    className="property-empty"
                  >
                    No property types found
                  </td>
                </tr>
              )}

            </tbody>

          </table>

        </div>

        {/* Pagination */}
        <div className="property-pagination">

          <span>
            Showing{" "}
            {filteredData.length === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}{" "}
            -{" "}
            {Math.min(
              currentPage * rowsPerPage,
              filteredData.length
            )}{" "}
            of {filteredData.length}
          </span>

          <div className="property-pagination-buttons">

            <button
              disabled={currentPage === 1}
              onClick={() =>
                setPage((prev) =>
                  Math.max(1, prev - 1)
                )
              }
            >
              Previous
            </button>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1
            ).map((pageNumber) => (
              <button
                key={pageNumber}
                className={
                  currentPage === pageNumber
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setPage(pageNumber)
                }
              >
                {pageNumber}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() =>
                setPage((prev) =>
                  Math.min(
                    totalPages,
                    prev + 1
                  )
                )
              }
            >
              Next
            </button>

          </div>

        </div>

      </div>

      {/* Mobile Cards */}
      <div className="property-mobile-list">

        {currentRows.length > 0 ? (
          currentRows.map((item) => (
            <div
              className="property-mobile-card"
              key={item.id}
            >

              <div className="property-mobile-top">

                <div className="property-name">

                  <div className="property-row-icon">
                    <Home size={16} />
                  </div>

                  <div>
                    <strong>{item.name}</strong>
                    <span>
                      {item.description}
                    </span>
                  </div>

                </div>

                <button
                  className={`property-status ${
                    item.status === "Active"
                      ? "active"
                      : "inactive"
                  }`}
                  onClick={() =>
                    toggleStatus(item.id)
                  }
                >
                  {item.status}
                </button>

              </div>

              <div className="property-mobile-actions">

                <button
                  className="mobile-property-edit"
                  onClick={() =>
                    handleEdit(item)
                  }
                >
                  <Pencil size={15} />
                  Edit
                </button>

                <button
                  className="mobile-property-delete"
                  onClick={() =>
                    deletePropertyType(item.id)
                  }
                >
                  <Trash2 size={15} />
                  Delete
                </button>

              </div>

            </div>
          ))
        ) : (
          <div className="property-mobile-empty">
            No property types found
          </div>
        )}

      </div>

    </div>
  );
}